import deepSignApi from '@/api/deepsign/deepsignApi';
import type { AxiosRequestConfig } from 'axios';
import type { EnvelopeFile } from '@/types/deepsign/EnvelopeFile';
import type { EnvelopeFileAdd } from '@/types/deepsign/EnvelopeFileAdd';

//TODO @daniel.henkiel missing api definition - not yet part of @deepcloud/api-deep-sign
/**
 * Add a new file to a document (envelope).
 *
 * `POST /api/v1/documents/{documentId}/files`
 *
 * The payload is sent as multipart/form-data with two required parts: a `data`
 * JSON part ({@link EnvelopeFileAdd}) and the binary `file`.
 *
 * Notes:
 * - `DocumentPolicy.canAddFile` required
 * - Limit: 20 files per document
 * - The maximum document size (over all files) must not exceed 40 MB
 *
 * @param documentId The document (envelope) the file is added to
 * @param file The binary content of the file
 * @param data Options for the added file (e.g. predefined-data scanning)
 * @param config Optional axios request config
 */
export const addFileToEnvelope = (
  documentId: string,
  file: File,
  data: EnvelopeFileAdd = {},
  config?: AxiosRequestConfig
) => {
  const formData = new FormData();
  formData.append(
    'data',
    new Blob([JSON.stringify(data)], { type: 'application/json' })
  );
  formData.append('file', file, file.name);

  return deepSignApi.post<EnvelopeFile>(
    `documents/${documentId}/files`,
    formData,
    {
      ...config,
      headers: {
        'content-type': 'multipart/form-data',
        ...config?.headers
      }
    }
  );
};

//TODO @daniel.henkiel missing api definition - not yet part of @deepcloud/api-deep-sign
/**
 * Delete a file from a document (envelope).
 *
 * `DELETE /api/v1/documents/{documentId}/files/{fileId}`
 *
 * Notes:
 * - `FilePolicy.canDelete` required
 *
 * @param documentId The document (envelope) the file belongs to
 * @param fileId The file to delete
 * @param config Optional axios request config
 */
export const deleteEnvelopeFile = (
  documentId: string,
  fileId: string,
  config?: AxiosRequestConfig
) =>
  deepSignApi.delete<void>(`documents/${documentId}/files/${fileId}`, config);

//TODO @daniel.henkiel missing api definition - not yet part of @deepcloud/api-deep-sign
/**
 * Set the file order of a document (envelope).
 *
 * `PUT /api/v1/documents/{documentId}/files/order`
 *
 * Notes:
 * - `DocumentPolicy.canModifyFileOrder` required
 * - The ordered list must contain all available fileIds.
 *
 * @param documentId The document (envelope) whose file order is set
 * @param fileIds Ordered list of fileIds (all files must be provided)
 * @param config Optional axios request config
 */
export const updateEnvelopeFileOrder = (
  documentId: string,
  fileIds: string[],
  config?: AxiosRequestConfig
) =>
  deepSignApi.put<void>(`documents/${documentId}/files/order`, fileIds, config);

/**
 * Fetch a single file of a document (envelope).
 *
 * `GET /api/v1/documents/{documentId}/files/{fileId}`
 *
 * @param documentId The document (envelope) the file belongs to
 * @param fileId The file to fetch
 * @param config Optional axios request config
 */
export const getEnvelopeFile = (
  documentId: string,
  fileId: string,
  config?: AxiosRequestConfig
) =>
  deepSignApi.get<EnvelopeFile>(
    `documents/${documentId}/files/${fileId}`,
    config
  );
